/**
 * The account picker, with the inline "create new account" escape hatch.
 *
 * Search goes through **`POST /api/search` with the term in the body** (§9) —
 * the shareable URL may carry a term for client state, the request must not,
 * because API Gateway's access-log format excludes the query string precisely
 * so search terms never reach it.
 *
 * Creation carries §6.8's **duplicate guard: warn with candidates, never
 * block.** The trigram match on `accounts(name)` is a hint, not a gate — the
 * real file contains legitimately similar names ("BAE Systems Australia
 * Limited" beside "BAE Systems plc") and refusing them would be worse than a
 * duplicate.
 *
 * **A just-created account is pinned into the options.** `Combobox` resolves
 * the label it displays with `options.find((o) => o.value === value)`, and the
 * options here are `/api/search` hits — which do not contain an account
 * created a moment ago. Selecting one without pinning it leaves the field
 * holding the id and rendering its placeholder, which reads as "nothing
 * happened". Two doors lead here, so both are covered: the inline create row
 * (pinned from this component's own state) and a create control outside the
 * picker (passed back in as `created`).
 */

import { useMemo, useState } from 'react';
import type { GlobalSearchHit } from '@menlo/shared';
import { Dialog } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Field } from '@/components/ui/label';
import { Combobox, type Option } from '@/features/pickers/Combobox';
import { useApiMutation, useApiPost } from '@/lib/queries';
import { useReferenceData } from '@/lib/reference';
import { SEGMENTS, REGIONS, CHANNEL_TYPES } from '@menlo/shared';
import { humanize } from '@/lib/format';
import { normaliseCountry, normaliseWebsite } from '@/lib/account-fields';

export function AccountPicker({
  value,
  onChange,
  id,
  allowCreate = true,
  created = null,
  current = null,
  exclude = [],
  placeholder = 'Search accounts…',
}: {
  value: string | null;
  onChange: (value: string | null, created?: { id: string; name: string }) => void;
  id?: string;
  allowCreate?: boolean;
  /**
   * An account created outside this picker — by a `New account` button beside
   * the field — so the field can show its name before `/api/search` knows it
   * exists. A consumer that never creates one elsewhere can ignore it.
   */
  created?: { id: string; name: string } | null;
  /**
   * The account the field already holds when it opens — the parent on the
   * edit form — so it shows a name rather than the placeholder before any
   * search has returned it.
   */
  current?: { id: string; name: string } | null;
  /** Ids never offered: the account itself, when picking its parent. */
  exclude?: readonly string[];
  placeholder?: string;
}): React.ReactElement {
  const [term, setTerm] = useState('');
  const [creating, setCreating] = useState<string | null>(null);
  /** Accounts created through the inline row, for the same reason as `created`. */
  const [createdHere, setCreatedHere] = useState<{ id: string; name: string }[]>([]);
  /**
   * The search hit last chosen, kept for the same reason: a later search that
   * no longer returns it must not blank the field.
   */
  const [chosen, setChosen] = useState<{ id: string; name: string } | null>(null);

  // Recent accounts seed the list before anyone types, so the common case is
  // a click rather than a search.
  const recent = useApiPost<{ items: GlobalSearchHit[] }>(['account-search', term], '/api/search', {
    term: term.trim() === '' ? 'a' : term.trim(),
    limit: 20,
  });

  const options: Option[] = useMemo(() => {
    const hits = (recent.data?.items ?? []).filter(
      (hit) => hit.kind === 'account' && !exclude.includes(hit.id),
    );
    const found: Option[] = hits.map((hit) => ({
      value: hit.id,
      label: hit.label,
      sublabel: hit.sublabel ?? undefined,
    }));

    // Pinned first, deduplicated against each other and against the search
    // results — once the account turns up in a search it needs no pin.
    const pinned: Option[] = [...(created === null ? [] : [created]), ...createdHere]
      .filter(
        (account, index, all) =>
          all.findIndex((other) => other.id === account.id) === index &&
          !found.some((option) => option.value === account.id),
      )
      .map((account) => ({ value: account.id, label: account.name, sublabel: 'Just created' }));

    const held: Option[] = [chosen, current]
      .filter((account): account is { id: string; name: string } => account !== null)
      .filter(
        (account, index, all) =>
          all.findIndex((other) => other.id === account.id) === index &&
          !found.some((option) => option.value === account.id) &&
          !pinned.some((option) => option.value === account.id),
      )
      .map((account) => ({ value: account.id, label: account.name }));

    return [...held, ...pinned, ...found];
  }, [recent.data, created, createdHere, current, chosen, exclude]);

  return (
    <>
      <Combobox
        id={id}
        value={value}
        onChange={(next) => {
          const option = options.find((candidate) => candidate.value === next);
          if (next !== null && option !== undefined) setChosen({ id: next, name: option.label });
          onChange(next);
        }}
        options={options}
        onSearch={setTerm}
        placeholder={placeholder}
        {...(allowCreate
          ? { onCreate: (name: string) => setCreating(name), createLabel: 'Create account' }
          : {})}
      />
      {creating !== null && (
        <CreateAccountDialog
          initialName={creating}
          onClose={() => setCreating(null)}
          onCreated={(account) => {
            setCreating(null);
            setCreatedHere((all) => [...all, account]);
            onChange(account.id, account);
          }}
        />
      )}
    </>
  );
}

/**
 * *Parent account*, on the create and edit forms (session 6). A site or
 * location is its own account under its company, and the company's page rolls
 * the sites up. The server refuses a parent that would make the account its
 * own ancestor; `exclude` only spares the user offering the account itself.
 */
export function ParentAccountField({
  value,
  onChange,
  current = null,
  exclude = [],
}: {
  value: string | null;
  onChange: (value: string | null) => void;
  current?: { id: string; name: string } | null;
  exclude?: readonly string[];
}): React.ReactElement {
  return (
    <div className="space-y-1">
      <Field
        label="Parent account"
        hint="For a site or location: the company it belongs to. The parent's page rolls up its sites' contacts, deals and totals."
      >
        <AccountPicker
          value={value}
          onChange={(next) => onChange(next)}
          allowCreate={false}
          current={current}
          exclude={exclude}
          placeholder="None — a top-level account"
        />
      </Field>
      {value !== null && (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs"
          onClick={() => onChange(null)}
        >
          Remove parent
        </Button>
      )}
    </div>
  );
}

export function CreateAccountDialog({
  initialName,
  initialParent = null,
  onClose,
  onCreated,
}: {
  initialName: string;
  /** Pre-fills *Parent account* — "Add a site" on the parent's page. */
  initialParent?: { id: string; name: string } | null;
  onClose: () => void;
  onCreated: (account: { id: string; name: string }) => void;
}): React.ReactElement {
  const reference = useReferenceData();
  const [name, setName] = useState(initialName);
  const [segment, setSegment] = useState<string>('other');
  const [region, setRegion] = useState<string>('AMER');
  const [channelType, setChannelType] = useState<string>('direct');
  const [repFirmId, setRepFirmId] = useState<string>('');
  const [website, setWebsite] = useState('');
  const [hqCountry, setHqCountry] = useState('');
  const [parentId, setParentId] = useState<string | null>(initialParent?.id ?? null);

  /**
   * RF-0003: both columns carry a check constraint, so the shape is settled
   * here rather than by a `23514` from Postgres. Live rather than on submit —
   * the field says what is wrong while it is still in front of the user.
   */
  const websiteValue = normaliseWebsite(website);
  const countryValue = normaliseCountry(hqCountry);
  const invalid = websiteValue.error !== null || countryValue.error !== null;

  /** §6.8: warn with candidates, never block. */
  const candidates = useApiPost<{ items: GlobalSearchHit[] }>(
    ['account-dupes', name],
    '/api/search',
    { term: name.trim() === '' ? 'zzzz' : name.trim(), limit: 5 },
  );
  const similar = (candidates.data?.items ?? []).filter((hit) => hit.kind === 'account');

  const create = useApiMutation<{ account: { id: string; name: string } }, void>(
    () => ({
      path: '/api/accounts',
      method: 'POST',
      body: {
        name: name.trim(),
        segment,
        region,
        channel_type: channelType,
        ...(repFirmId === '' ? {} : { rep_firm_id: repFirmId }),
        ...(websiteValue.value === null ? {} : { website: websiteValue.value }),
        ...(countryValue.value === null ? {} : { hq_country: countryValue.value }),
        ...(parentId === null ? {} : { parent_account_id: parentId }),
      },
    }),
    {
      invalidate: [['accounts']],
      onSuccess: (data) => onCreated(data.account),
    },
  );

  return (
    <Dialog
      open
      onClose={onClose}
      title="New account"
      description="The minimum to create one. Everything else is editable on the account page."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => create.mutate()}
            disabled={name.trim() === '' || invalid || create.isPending}
          >
            Create account
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Name">
          <Input value={name} onChange={(event) => setName(event.target.value)} autoFocus />
        </Field>

        <ParentAccountField value={parentId} onChange={setParentId} current={initialParent} />

        {similar.length > 0 && (
          <div className="rounded-md border border-amber-600/40 bg-amber-50 p-3 text-xs text-amber-950">
            <p className="font-medium">
              {similar.length} similar account{similar.length === 1 ? '' : 's'} already exist
            </p>
            <ul className="mt-1 list-disc space-y-0.5 pl-4">
              {similar.map((hit) => (
                <li key={hit.id}>{hit.label}</li>
              ))}
            </ul>
            <p className="mt-1">
              This is a warning, not a block — subsidiaries and regional entities legitimately look
              alike.
            </p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Segment">
            <Select value={segment} onChange={(event) => setSegment(event.target.value)}>
              {SEGMENTS.map((value) => (
                <option key={value} value={value}>
                  {humanize(value)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Region">
            <Select value={region} onChange={(event) => setRegion(event.target.value)}>
              {REGIONS.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Channel">
            <Select value={channelType} onChange={(event) => setChannelType(event.target.value)}>
              {CHANNEL_TYPES.map((value) => (
                <option key={value} value={value}>
                  {humanize(value)}
                </option>
              ))}
            </Select>
          </Field>
          {channelType !== 'direct' && (
            <Field label="Rep firm" hint="Sets the net factor that converts resale to net.">
              <Select value={repFirmId} onChange={(event) => setRepFirmId(event.target.value)}>
                <option value="">—</option>
                {reference.rep_firms.map((firm) => (
                  <option key={firm.id} value={firm.id}>
                    {firm.name}
                  </option>
                ))}
              </Select>
            </Field>
          )}

          {/*
            Both columns have existed since Phase 4 and were set by nothing.
            The error sits OUTSIDE `Field`'s implicit label, or it would be
            read out as part of the control's accessible name.
          */}
          <div className="space-y-1">
            <Field label="Website" hint="https:// is added if you leave it off.">
              <Input
                value={website}
                placeholder="example.com"
                onChange={(event) => setWebsite(event.target.value)}
              />
            </Field>
            {websiteValue.error !== null && (
              <p className="text-[11px] font-medium text-red-700">{websiteValue.error}</p>
            )}
          </div>
          <div className="space-y-1">
            <Field label="HQ country" hint="Two-letter ISO code; case does not matter.">
              <Input
                value={hqCountry}
                placeholder="US"
                onChange={(event) => setHqCountry(event.target.value)}
              />
            </Field>
            {countryValue.error !== null && (
              <p className="text-[11px] font-medium text-red-700">{countryValue.error}</p>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
}
